// Profile.jsx
import React, { useContext, useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import {
  MoreVertical,
  User,
  Edit,
  Trash2,
  CheckCircle2,
  MessageCircle,
  Cpu,
} from "lucide-react";
import {
  FiEdit,
  FiLock,
  FiLogOut,
  FiGlobe,
  FiGithub,
  FiLinkedin,
  FiTwitter,
  FiInstagram,
  FiFacebook,
} from "react-icons/fi";
import { SiHashnode } from "react-icons/si";
import {
  FaYoutube,
  FaMedium,
  FaDev,
  FaDribbble,
  FaBehance,
  FaReddit,
  FaSlack,
  FaDiscord,
  FaTelegram,
  FaWhatsapp,
} from "react-icons/fa";

import { UserContext } from "../context/UserContext";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  deleteDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  addDoc,
  orderBy,
} from "firebase/firestore";
import { db, auth } from "../firebase/firebaseConfig";
import { signOut } from "firebase/auth";
import { toast } from "react-toastify";
import LogoutModal from "../components/modal/LogoutModal";
import { getTimeAgo } from "../utils/helper";

/* ----------------------------- social icons map ----------------------------- */
const socialIcons = {
  github: <FiGithub />,
  linkedin: <FiLinkedin />,
  twitter: <FiTwitter />,
  facebook: <FiFacebook />,
  instagram: <FiInstagram />,
  youtube: <FaYoutube />,
  medium: <FaMedium />,
  devto: <FaDev />,
  hashnode: <SiHashnode />,
  dribbble: <FaDribbble />,
  behance: <FaBehance />,
  stackoverflow: <FiGlobe />,
  reddit: <FaReddit />,
  slack: <FaSlack />,
  discord: <FaDiscord />,
  telegram: <FaTelegram />,
  whatsapp: <FaWhatsapp />,
  portfolio: <FiGlobe />,
  website: <FiGlobe />,
};

/* --------------------------- animated subtle background --------------------------- */
const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.9; }
  50% { transform: scale(1.02); opacity: 1; }
  100% { transform: scale(1); opacity: 0.9; }
`;

/* -------------------------------- STYLES -------------------------------- */
const Page = styled.div`
  min-height: 100vh;
  background: radial-gradient(circle at top left, #0b0617, #000);
  color: #fff;
  padding: 24px;
  box-sizing: border-box;
  @media (max-width: 768px) {
    padding: 14px;
  }
`;

/* main layout -> left info + right content on desktop, stacked on mobile */
const Layout = styled.div`
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: 20px;
  align-items: start;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 992px) {
    grid-template-columns: 1fr; /* ✅ stack on tablet/mobile */
  }
`;

/* Left panel (profile summary) */
const LeftCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(155, 92, 255, 0.12);
  border-radius: 14px;
  padding: 18px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  gap: 14px;
  animation: ${pulse} 8s ease-in-out infinite;
`;

/* Right column containing multiple stacked cards */
const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

/* Smaller cards to reuse for sections */
const Card = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(155, 92, 255, 0.08);
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.55);
`;

/* Top area in left card */
const ProfileRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const AvatarWrap = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #9b5cff, #c59fff);
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
`;

const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Name = styled.h2`
  margin: 0;
  font-size: 20px;
  color: #fff;
`;

const Email = styled.p`
  margin: 4px 0 0;
  color: #bfc7da;
  font-size: 13px;
`;

/* follow/chat buttons */
const RowButtons = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 8px;
  flex-wrap: wrap;
`;

const GradientButton = styled.button`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  border: none;
  color: white;
  padding: 10px 14px;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(155, 92, 255, 0.18);
  transition: transform 0.14s ease, box-shadow 0.14s ease;
  display: inline-flex;
  gap: 8px;
  align-items: center;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 34px rgba(155, 92, 255, 0.22);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const GhostButton = styled.button`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.04);
  color: #d6d6df;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 700;
  display: inline-flex;
  gap: 8px;
  align-items: center;
  transition: all 0.14s ease;

  &:hover {
    transform: translateY(-2px);
    background: rgba(155, 92, 255, 0.07);
    color: #fff;
  }
`;

/* stats */
const Stats = styled.div`
  display: flex;
  flex-wrap: wrap; /* ✅ allow wrapping */
  gap: 10px;
  margin-top: 10px;

  @media (max-width: 768px) {
    justify-content: space-between;
  }
`;

const Stat = styled.div`
  flex: 1 1 100px; /* ✅ auto shrink/grow with min width */
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.02),
    rgba(255, 255, 255, 0.01)
  );
  border: 1px solid rgba(255, 255, 255, 0.05);
  padding: 10px;
  border-radius: 10px;
  text-align: center;
  min-width: 90px;
  box-sizing: border-box;

  h4 {
    margin: 0;
    font-size: 18px;
    color: #fff;
  }

  span {
    display: block;
    margin-top: 6px;
    color: #bfc7da;
    font-size: 13px;
  }

  @media (max-width: 480px) {
    flex: 1 1 80px; /* ✅ smaller min width on phones */
    h4 {
      font-size: 16px;
    }
    span {
      font-size: 12px;
    }
  }
`;

/* section header */
const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;

  h3 {
    margin: 0;
    color: #c59fff;
  }
`;

/* projects grid */
const ProjectsGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
`;

/* project card */
const ProjectCard = styled.div`
  background: rgba(255, 255, 255, 0.015);
  border: 1px solid rgba(255, 255, 255, 0.03);
  padding: 12px;
  border-radius: 10px;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 18px 40px rgba(155, 92, 255, 0.06);
  }

  h4 {
    margin: 0 0 6px 0;
    color: #fff;
  }
  p {
    margin: 0;
    color: #cfcfd6;
    font-size: 13px;
  }
  a {
    color: #9b5cff;
    font-weight: 700;
    text-decoration: none;
    font-size: 13px;
  }
`;

/* issues list */
const IssuesGrid = styled.div`
  display: grid;
  gap: 12px;
`;

const IssueCardSmall = styled.div`
  background: rgba(255, 255, 255, 0.015);
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.03);
  padding: 12px;
  transition: transform 0.12s ease, box-shadow 0.12s ease;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 18px 40px rgba(155, 92, 255, 0.05);
  }

  h4 {
    margin: 0 0 6px 0;
    color: #fff;
    font-size: 15px;
  }
  p {
    margin: 0;
    color: #cfcfd6;
    font-size: 13px;
  }
`;

/* badges */
const BadgesRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Badge = styled.span`
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 12px;
  color: #fff;
  background: ${({ variant }) =>
    variant === "purple"
      ? "linear-gradient(90deg,#6c5ce7,#9b5cff)"
      : variant === "teal"
      ? "linear-gradient(90deg,#00cec9,#00fcc9)"
      : variant === "green"
      ? "linear-gradient(90deg,#34d399,#10b981)"
      : variant === "orange"
      ? "linear-gradient(90deg,#ffb86b,#ff7a7a)"
      : "#444"};
  transition: box-shadow 0.14s ease, transform 0.12s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 28px rgba(155, 92, 255, 0.12);
  }
`;

/* socials */
const SocialsRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  a {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.02);
    border: 1px solid rgba(255, 255, 255, 0.03);
    color: #cfcfd6;
    transition: transform 0.12s ease, background 0.12s ease;
    &:hover {
      transform: translateY(-4px);
      background: linear-gradient(90deg, #9b5cff, #c59fff);
      color: white;
      box-shadow: 0 14px 40px rgba(155, 92, 255, 0.12);
    }
  }
`;

/* small controls (tabs etc) */
const Tabs = styled.div`
  display: flex;
  gap: 8px;
`;

const Tab = styled.button`
  background: ${({ active }) =>
    active
      ? "linear-gradient(90deg,#6c5ce7,#9b5cff)"
      : "rgba(255,255,255,0.02)"};
  color: ${({ active }) => (active ? "#fff" : "#d6d6df")};
  padding: 8px 12px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-weight: 700;
  transition: all 0.12s ease;
  &:hover {
    transform: translateY(-3px);
  }
`;

/* modal basic */
const ConfirmModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;
const ConfirmModalBox = styled.div`
  background: #0e0e10;
  border-radius: 12px;
  padding: 18px;
  width: 92%;
  max-width: 420px;
  border: 1px solid rgba(155, 92, 255, 0.08);
`;

/* --------------------------- MAIN COMPONENT --------------------------- */
const Profile = () => {
  const { devId } = useParams();
  const { user } = useContext(UserContext) || {};
  const navigate = useNavigate();

  // data states (keep logic same)
  const [profileData, setProfileData] = useState(null);
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [isFollowing, setIsFollowing] = useState(false);

  const [activeTab, setActiveTab] = useState("all");
  const [userIssues, setUserIssues] = useState([]);
  const [projects, setProjects] = useState([]);
  const [skills, setSkills] = useState([]);
  const [socials, setSocials] = useState([]);
  const [loading, setLoading] = useState(true);

  const [openMenu, setOpenMenu] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showSolvedModal, setShowSolvedModal] = useState(false);
  const [solvedId, setSolvedId] = useState(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const suggestionsRef = useRef(null);

  useEffect(() => {
    if (!devId) return;
    fetchUserIssues();
    fetchProjects();
    getUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devId, user?.id]);

  /* fetch user issues */
  const fetchUserIssues = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, "codeIssues"),
        where("posterId", "==", devId),
        orderBy("createdAt", "desc")
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setUserIssues(data);
    } catch (err) {
      console.error("fetchUserIssues:", err);
    } finally {
      setLoading(false);
    }
  };

  /* projects */
  const fetchProjects = async () => {
    try {
      const q = query(
        collection(db, "users", devId, "projects"),
        orderBy("createdAt", "desc")
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setProjects(data);
    } catch (err) {
      console.error("fetchProjects:", err);
    }
  };

  /* user doc */
  const getUserData = async () => {
    try {
      const uDoc = await getDoc(doc(db, "users", devId));
      if (uDoc.exists()) {
        const d = uDoc.data();
        setProfileData(d);
        setSkills(d.skills || []);
        setSocials(d.socials || []);
        setFollowersCount(d.followers?.length || 0);
        setFollowingCount(d.following?.length || 0);
        setIsFollowing(user?.id ? d.followers?.includes(user.id) : false);
      } else {
        setProfileData(null);
      }
    } catch (err) {
      console.error("getUserData:", err);
    }
  };

  /* follow/unfollow */
  const handleFollowToggle = async () => {
    if (!user?.id) {
      navigate("/login");
      return;
    }
    try {
      const targetUserRef = doc(db, "users", devId);
      const currentUserRef = doc(db, "users", user.id);

      if (isFollowing) {
        await updateDoc(targetUserRef, { followers: arrayRemove(user.id) });
        await updateDoc(currentUserRef, { following: arrayRemove(devId) });
        setFollowersCount((p) => Math.max(0, p - 1));
        setIsFollowing(false);
      } else {
        await updateDoc(targetUserRef, { followers: arrayUnion(user.id) });
        await updateDoc(currentUserRef, { following: arrayUnion(devId) });
        setFollowersCount((p) => p + 1);
        setIsFollowing(true);
      }
    } catch (err) {
      console.error("Follow error:", err);
      toast.error("Unable to update follow.");
    }
  };

  /* chat */
  const handleChat = async () => {
    if (!user?.id) {
      navigate("/login");
      return;
    }
    try {
      // look for existing chat where both are members
      const chatsRef = collection(db, "chats");
      const q = query(chatsRef, where("members", "array-contains", user.id));
      const snapshot = await getDocs(q);

      let chatId = null;
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.members.includes(devId)) {
          chatId = docSnap.id;
        }
      });

      if (!chatId) {
        const newChat = await addDoc(collection(db, "chats"), {
          members: [user.id, devId],
          membersInfo: {
            [user.id]: {
              name: user.name,
              email: user.email,
              profilePic: user.profilePic || null,
            },
            [devId]: {
              name: profileData?.name || "Unnamed User",
              email: profileData?.email || null,
              profilePic: profileData?.profilePic || null,
            },
          },
          createdAt: serverTimestamp(),
          messages: [],
        });
        chatId = newChat.id;
      }

      navigate("/my-chats");
    } catch (err) {
      console.error("Chat error:", err);
      toast.error("Unable to open chat.");
    }
  };

  /* delete issue */
  const confirmDelete = async () => {
    setShowDeleteModal(false);
    try {
      if (deleteId) {
        await deleteDoc(doc(db, "codeIssues", deleteId));
        fetchUserIssues();
        toast.success("Issue deleted.");
      }
    } catch (err) {
      console.error("confirmDelete:", err);
      toast.error("Delete failed.");
    } finally {
      setDeleteId(null);
    }
  };

  /* mark solved */
  const confirmSolved = async () => {
    setShowSolvedModal(false);
    try {
      if (solvedId) {
        await updateDoc(doc(db, "codeIssues", solvedId), {
          status: "SOLVED",
          solvedAt: serverTimestamp(),
        });
        fetchUserIssues();
        toast.success("Marked as solved.");
      }
    } catch (err) {
      console.error("confirmSolved:", err);
      toast.error("Update failed.");
    } finally {
      setSolvedId(null);
    }
  };

  const handleChangePassword = () => {
    if (!user?.id) {
      navigate("/login");
      return;
    }
    toast.info("Change password feature coming soon!");
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login");
      toast.success("Logged out.");
    } catch (err) {
      console.error("Logout error:", err);
      toast.error("Logout failed.");
    }
  };

  /* filtered issues by tab */
  const filteredIssues =
    activeTab === "all"
      ? userIssues
      : userIssues.filter((issue) =>
          activeTab === "solved"
            ? issue.status === "SOLVED"
            : issue.status !== "SOLVED"
        );

  /* small helper to display tech/difficulty in nicer case */
  const pretty = (s) => (s ? String(s).replace(/_/g, " ") : "Unknown");

  return (
    <Page>
      <Layout>
        {/* LEFT: profile summary card */}
        <LeftCard>
          <ProfileRow>
            <AvatarWrap>
              {profileData?.profilePic ? (
                <AvatarImg
                  src={profileData.profilePic}
                  alt={profileData?.name}
                />
              ) : (
                <User size={34} color="#fff" />
              )}
            </AvatarWrap>

            <div style={{ flex: 1 }}>
              <Name>{profileData?.name || "Unnamed User"}</Name>
              <Email>{profileData?.email || "No email available"}</Email>

              <Stats>
                <Stat>
                  <h4>{followersCount}</h4>
                  <span>Followers</span>
                </Stat>
                <Stat>
                  <h4>{followingCount}</h4>
                  <span>Following</span>
                </Stat>
                <Stat>
                  <h4>{userIssues.length}</h4>
                  <span>Issues</span>
                </Stat>
              </Stats>

              <RowButtons>
                {user?.id !== devId && (
                  <>
                    <GradientButton onClick={handleFollowToggle}>
                      {isFollowing ? "Unfollow" : "Follow"}
                    </GradientButton>

                    <GhostButton onClick={handleChat}>
                      <MessageCircle size={16} /> Chat
                    </GhostButton>
                  </>
                )}

                {user?.id === devId && (
                  <>
                    <GhostButton
                      onClick={() => navigate("/profile/edit/" + user.id)}
                    >
                      <FiEdit /> Edit Profile
                    </GhostButton>
                    <GhostButton onClick={handleChangePassword}>
                      <FiLock /> Change Password
                    </GhostButton>
                    <GhostButton onClick={() => setShowLogoutModal(true)}>
                      <FiLogOut /> Logout
                    </GhostButton>
                  </>
                )}
              </RowButtons>
            </div>
          </ProfileRow>

          {/* bio / about */}
          <div>
            <h4 style={{ color: "#c59fff", margin: "0 0 8px 0" }}>About</h4>
            <p style={{ margin: 0, color: "#cfcfd6" }}>
              {profileData?.bio || "No bio added."}
            </p>
          </div>

          {/* skills */}
          <div>
            <h4 style={{ color: "#c59fff", margin: "8px 0" }}>Skills</h4>
            {skills.length > 0 ? (
              <BadgesRow>
                {skills.map((s, i) => (
                  <Badge key={i} variant="purple">
                    {s}
                  </Badge>
                ))}
              </BadgesRow>
            ) : (
              <div style={{ color: "#9aa0b3" }}>No skills added.</div>
            )}
          </div>

          {/* socials */}
          <div>
            <h4 style={{ color: "#c59fff", margin: "8px 0" }}>Socials</h4>
            {socials.length > 0 ? (
              <SocialsRow>
                {socials.map(
                  (s, i) =>
                    s.url && (
                      <a
                        key={i}
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                        title={s.name}
                      >
                        {socialIcons[s.name.toLowerCase()] || <FiGlobe />}
                      </a>
                    )
                )}
              </SocialsRow>
            ) : (
              <div style={{ color: "#9aa0b3" }}>No socials added.</div>
            )}
          </div>
        </LeftCard>

        {/* RIGHT: projects, issues, etc */}
        <RightColumn>
          {/* PROJECTS */}
          <Card>
            <SectionHeader>
              <h3>Projects</h3>
              {user?.id === devId && (
                <GhostButton onClick={() => navigate(`/profile/edit/projects`)}>
                  <FiEdit /> Edit
                </GhostButton>
              )}
            </SectionHeader>

            {projects.length > 0 ? (
              <ProjectsGrid>
                {projects.map((proj) => (
                  <ProjectCard key={proj.id}>
                    {proj.imageUrl && (
                      <div
                        style={{
                          width: "100%",
                          height: 120,
                          overflow: "hidden",
                          borderRadius: 8,
                        }}
                      >
                        <img
                          src={proj.imageUrl}
                          alt={proj.title}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </div>
                    )}
                    <div style={{ marginTop: 10 }}>
                      <h4>{proj.title}</h4>
                      {proj.link && (
                        <a href={proj.link} target="_blank" rel="noreferrer">
                          {proj.link}
                        </a>
                      )}
                      <p style={{ marginTop: 8 }}>
                        {proj.description?.length > 120
                          ? proj.description.slice(0, 120) + "..."
                          : proj.description}
                      </p>
                    </div>
                  </ProjectCard>
                ))}
              </ProjectsGrid>
            ) : (
              <div>No projects added yet.</div>
            )}
          </Card>

          {/* ISSUES */}
          <Card>
            <SectionHeader>
              <h3>Issues</h3>
              <Tabs>
                <Tab
                  active={activeTab === "all"}
                  onClick={() => setActiveTab("all")}
                >
                  All
                </Tab>
                <Tab
                  active={activeTab === "solved"}
                  onClick={() => setActiveTab("solved")}
                >
                  Solved
                </Tab>
                <Tab
                  active={activeTab === "unsolved"}
                  onClick={() => setActiveTab("unsolved")}
                >
                  Unsolved
                </Tab>
              </Tabs>
            </SectionHeader>

            {loading ? (
              <div style={{ padding: 12, color: "#cfcfd6" }}>Loading...</div>
            ) : filteredIssues.length > 0 ? (
              <IssuesGrid>
                {filteredIssues.map((issue) => (
                  <IssueCardSmall key={issue.id}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 8,
                        alignItems: "start",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <h4>{issue.title}</h4>
                        <p style={{ marginTop: 6 }}>
                          {issue.description?.length > 180
                            ? issue.description.slice(0, 180) + "..."
                            : issue.description}
                        </p>
                      </div>

                      {/* per-issue menu for owner */}
                      {user?.id === devId && (
                        <div style={{ marginLeft: 10, position: "relative" }}>
                          <button
                            onClick={() =>
                              setOpenMenu(
                                openMenu === issue.id ? null : issue.id
                              )
                            }
                            style={{
                              background: "transparent",
                              border: "none",
                              color: "#cfcfd6",
                              cursor: "pointer",
                            }}
                          >
                            <MoreVertical size={18} />
                          </button>

                          {openMenu === issue.id && (
                            <div
                              style={{
                                position: "absolute",
                                right: 0,
                                top: 26,
                                background: "rgba(15,15,16,0.95)",
                                border: "1px solid rgba(155,92,255,0.06)",
                                borderRadius: 8,
                                padding: 6,
                                zIndex: 40,
                              }}
                            >
                              <button
                                onClick={() =>
                                  navigate(`/edit-code-issue/${issue.id}`)
                                }
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  alignItems: "center",
                                  padding: 8,
                                  background: "transparent",
                                  border: "none",
                                  color: "#fff",
                                  cursor: "pointer",
                                }}
                              >
                                <Edit size={14} /> Edit
                              </button>
                              <button
                                onClick={() => {
                                  setDeleteId(issue.id);
                                  setShowDeleteModal(true);
                                  setOpenMenu(null);
                                }}
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  alignItems: "center",
                                  padding: 8,
                                  background: "transparent",
                                  border: "none",
                                  color: "#ff7a7a",
                                  cursor: "pointer",
                                }}
                              >
                                <Trash2 size={14} /> Delete
                              </button>
                              {issue.status !== "SOLVED" && (
                                <button
                                  onClick={() => {
                                    setSolvedId(issue.id);
                                    setShowSolvedModal(true);
                                    setOpenMenu(null);
                                  }}
                                  style={{
                                    display: "flex",
                                    gap: 8,
                                    alignItems: "center",
                                    padding: 8,
                                    background: "transparent",
                                    border: "none",
                                    color: "#aaffc9",
                                    cursor: "pointer",
                                  }}
                                >
                                  <CheckCircle2 size={14} /> Mark Solved
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div
                      style={{
                        marginTop: 10,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <BadgesRow>
                        <Badge
                          variant={
                            issue.status === "SOLVED" ? "green" : "orange"
                          }
                        >
                          {issue.status}
                        </Badge>
                        <Badge variant="teal">{pretty(issue.technology)}</Badge>
                        {issue.difficulty && (
                          <Badge variant="purple">
                            {pretty(issue.difficulty)}
                          </Badge>
                        )}
                      </BadgesRow>

                      <div style={{ color: "#9aa0b3", fontSize: 12 }}>
                        {issue.createdAt?.seconds
                          ? new Date(
                              issue.createdAt.seconds * 1000
                            ).toLocaleDateString()
                          : ""}
                      </div>
                    </div>
                  </IssueCardSmall>
                ))}
              </IssuesGrid>
            ) : (
              <div>No issues posted yet.</div>
            )}
          </Card>

          {/* bottom: Optional extra cards (skills repeated or other info) */}
          <Card>
            <SectionHeader>
              <h3>About & Activity</h3>
              <div />
            </SectionHeader>

            <div style={{ color: "#cfcfd6", marginBottom: 10 }}>
              <strong>Joined:</strong>{" "}
              {profileData?.createdAt?.seconds
                ? new Date(
                    profileData.createdAt.seconds * 1000
                  ).toLocaleDateString()
                : "—"}
            </div>

            <div style={{ color: "#9aa0b3", fontSize: 14 }}>
              <p>
                <strong>Last seen:</strong>{" "}
                {profileData?.lastSeen
                  ? getTimeAgo(profileData.lastSeen)
                  : "Recently"}
              </p>
              <p>
                {profileData?.location ? (
                  <>
                    <strong>Location:</strong> {profileData.location}
                  </>
                ) : null}
              </p>
            </div>
          </Card>
        </RightColumn>
      </Layout>

      {/* Delete Modal */}
      {showDeleteModal && (
        <ConfirmModalOverlay>
          <ConfirmModalBox>
            <h3 style={{ marginTop: 0, color: "#c59fff" }}>Confirm Delete</h3>
            <p>
              Are you sure you want to delete this issue? This action cannot be
              undone.
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                marginTop: 14,
              }}
            >
              <GhostButton onClick={() => setShowDeleteModal(false)}>
                Cancel
              </GhostButton>
              <GradientButton onClick={confirmDelete}>Delete</GradientButton>
            </div>
          </ConfirmModalBox>
        </ConfirmModalOverlay>
      )}

      {/* Solved Modal */}
      {showSolvedModal && (
        <ConfirmModalOverlay>
          <ConfirmModalBox>
            <h3 style={{ marginTop: 0, color: "#c59fff" }}>Mark as Solved</h3>
            <p>
              Mark this issue as solved? This will update its status for all
              users.
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                marginTop: 14,
              }}
            >
              <GhostButton onClick={() => setShowSolvedModal(false)}>
                Cancel
              </GhostButton>
              <GradientButton onClick={confirmSolved}>Confirm</GradientButton>
            </div>
          </ConfirmModalBox>
        </ConfirmModalOverlay>
      )}

      {/* Logout modal */}
      {showLogoutModal && (
        <LogoutModal
          onClose={() => setShowLogoutModal(false)}
          onLogout={handleLogout}
        />
      )}
    </Page>
  );
};

export default Profile;
